import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Loader2, Send, User, AlertCircle, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { contactApi, transactionApi } from '@/services/api'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import toast from 'react-hot-toast'
import type { Contact } from '@/types'
import { useWallets } from '@/hooks/useWallets'
import { formatCurrency } from '@/lib/utils'

interface SendMoneyModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

export function SendMoneyModal({ isOpen, onClose, onSuccess }: SendMoneyModalProps) {
  const { t } = useTranslation()
  const { wallets } = useWallets()
  const [contacts, setContacts] = useState<Contact[]>([])
  const [isLoadingContacts, setIsLoadingContacts] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  const [isCheckingWallet, setIsCheckingWallet] = useState(false)
  const [hasWallet, setHasWallet] = useState<boolean | null>(null)
  const [amount, setAmount] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [isSending, setIsSending] = useState(false)

  // Load contacts when modal opens
  useEffect(() => {
    if (isOpen) {
      loadContacts()
      // Reset state
      setSelectedContact(null)
      setHasWallet(null)
      setAmount('')
      setDescription('')
      setSearchTerm('')
    }
  }, [isOpen])

  const loadContacts = async () => {
    setIsLoadingContacts(true)
    try {
      const response = await contactApi.getContacts()
      const data = response.data.data || response.data
      let contactsData: Contact[] = []
      
      if (Array.isArray(data)) {
        contactsData = data
      } else if (data && typeof data === 'object' && 'results' in data) {
        contactsData = (data as any).results || []
      }
      
      setContacts(contactsData)
    } catch (error) {
      console.error('Error loading contacts:', error)
      toast.error(t('contacts.loadError') || 'Erro ao carregar contactos')
      setContacts([])
    } finally {
      setIsLoadingContacts(false)
    }
  }

  const handleSelectContact = async (contact: Contact) => {
    setSelectedContact(contact)
    setIsCheckingWallet(true)
    setHasWallet(null)

    try {
      // Try to find user by contact (phone or email)
      // This endpoint should check if the user exists and has wallets
      // For now, we'll try to send money and see if it works
      // In a real implementation, you'd have an endpoint like GET /users/by_contact?phone=xxx&email=xxx
      
      // Simulate checking - in real implementation, call an API endpoint
      // For now, we'll proceed to the send money step and handle error if user doesn't exist
      setHasWallet(true) // Assume has wallet - will be checked when sending
    } catch (error: any) {
      console.error('Error checking user wallet:', error)
      if (error.response?.status === 404 || error.response?.data?.error?.includes('not found')) {
        setHasWallet(false)
      } else {
        setHasWallet(null)
        toast.error(t('contacts.checkError') || 'Erro ao verificar utilizador')
      }
    } finally {
      setIsCheckingWallet(false)
    }
  }

  const handleSendMoney = async () => {
    if (!selectedContact) {
      toast.error(t('contacts.selectContact') || 'Por favor, selecione um contacto')
      return
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast.error(t('wallet.pleaseEnterAmount') || 'Por favor, insira um valor')
      return
    }

    const selectedWallet = wallets?.find(w => w.is_default) || wallets?.[0]
    if (!selectedWallet) {
      toast.error(t('wallet.noWallet') || 'Nenhuma carteira disponível')
      return
    }

    if (parseFloat(amount) > parseFloat(selectedWallet.available_balance)) {
      toast.error(t('wallet.insufficientBalance') || 'Saldo insuficiente')
      return
    }

    setIsSending(true)

    try {
      // Send money to contact
      // The API should handle finding the user by phone/email and checking if they have a wallet
      const response = await transactionApi.createTransaction({
        to_phone: selectedContact.phone_number ? `${selectedContact.country_code}${selectedContact.phone_number}` : undefined,
        to_email: selectedContact.email,
        amount: parseFloat(amount),
        currency: selectedWallet.currency,
        description: description || t('wallet.sendMoney') || 'Enviar dinheiro',
      })

      // Notifications are created automatically by the backend
      // No need to create them manually here

      toast.success(t('wallet.sendMoneySuccess') || 'Dinheiro enviado com sucesso!')
      onSuccess?.()
      onClose()
    } catch (error: any) {
      console.error('Error sending money:', error)
      
      // Check if error is because user doesn't have wallet
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || ''
      
      if (
        errorMessage.includes('not found') ||
        errorMessage.includes('no wallet') ||
        errorMessage.includes('not registered') ||
        errorMessage.includes('not in djelo') ||
        error.response?.status === 404
      ) {
        setHasWallet(false)
        toast.error(t('contacts.userNotInDjelo') || 'Este utilizador ainda não está na Djelo')
      } else {
        const finalErrorMessage = errorMessage || t('wallet.sendMoneyError') || 'Erro ao enviar dinheiro'
        toast.error(finalErrorMessage)
      }
    } finally {
      setIsSending(false)
    }
  }

  const filteredContacts = contacts.filter(contact => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    return (
      contact.name.toLowerCase().includes(search) ||
      contact.phone_number?.toLowerCase().includes(search) ||
      contact.email?.toLowerCase().includes(search)
    )
  })

  const selectedWallet = wallets?.find(w => w.is_default) || wallets?.[0]
  const maxAmount = selectedWallet ? parseFloat(selectedWallet.available_balance) : 0

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-[100]"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 pb-20 md:pb-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[calc(90vh-5rem)] md:max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-text-primary">
                  {t('wallet.sendMoney') || 'Enviar Dinheiro'}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  disabled={isSending}
                >
                  <X className="h-5 w-5 text-text-secondary" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {!selectedContact ? (
                  <>
                    {/* Search Contacts */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-text-secondary" />
                      <Input
                        type="text"
                        placeholder={t('contacts.search') || 'Pesquisar contactos...'}
                        value={searchTerm}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>

                    {/* Contacts List */}
                    {isLoadingContacts ? (
                      <div className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mb-2" />
                        <p className="text-text-secondary">{t('common.loading')}</p>
                      </div>
                    ) : filteredContacts.length === 0 ? (
                      <div className="text-center py-8">
                        <User className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-text-secondary">
                          {searchTerm 
                            ? t('contacts.noResults') || 'Nenhum resultado encontrado'
                            : t('contacts.noContacts') || 'Nenhum contacto encontrado'
                          }
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {filteredContacts.map((contact) => (
                          <motion.button
                            key={contact.id}
                            onClick={() => handleSelectContact(contact)}
                            className="w-full bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow text-left"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                <span className="text-primary font-bold text-sm">
                                  {contact.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-text-primary truncate">
                                  {contact.name}
                                </h3>
                                {contact.phone_number && (
                                  <p className="text-sm text-text-secondary truncate">
                                    {contact.country_code} {contact.phone_number}
                                  </p>
                                )}
                                {contact.email && (
                                  <p className="text-sm text-text-secondary truncate">
                                    {contact.email}
                                  </p>
                                )}
                              </div>
                              <Send className="h-5 w-5 text-primary" />
                            </div>
                          </motion.button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Selected Contact Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                            <span className="text-primary font-bold text-sm">
                              {selectedContact.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-text-primary">
                              {selectedContact.name}
                            </h3>
                            {selectedContact.phone_number && (
                              <p className="text-sm text-text-secondary">
                                {selectedContact.country_code} {selectedContact.phone_number}
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedContact(null)
                            setHasWallet(null)
                          }}
                          className="text-text-secondary hover:text-text-primary"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    {/* Check if user has wallet */}
                    {isCheckingWallet && (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-primary mr-2" />
                        <span className="text-text-secondary">{t('contacts.checkingUser') || 'A verificar utilizador...'}</span>
                      </div>
                    )}

                    {/* User doesn't have wallet message */}
                    {hasWallet === false && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-warning/10 border border-warning/20 rounded-lg p-4 flex items-start space-x-3"
                      >
                        <AlertCircle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="font-semibold text-warning mb-1">
                            {t('contacts.userNotRegistered') || 'Utilizador não registado'}
                          </h4>
                          <p className="text-sm text-text-secondary">
                            {t('contacts.userNotInDjelo') || 'Este utilizador ainda não está na Djelo. Convidar para se registar?'}
                          </p>
                        </div>
                      </motion.div>
                    )}

                    {/* Send Money Form */}
                    {hasWallet !== false && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-text-primary mb-2">
                            {t('wallet.amount') || 'Valor'}
                          </label>
                          <div className="relative">
                            <Input
                              type="number"
                              step="0.01"
                              min="0.01"
                              max={maxAmount}
                              placeholder="0.00"
                              value={amount}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
                              required
                              disabled={isSending}
                              className="pr-20"
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary">
                              {selectedWallet?.currency === 'STN' ? 'Db' : selectedWallet?.currency === 'EUR' ? '€' : '$'}
                            </span>
                          </div>
                          {selectedWallet && (
                            <p className="text-xs text-text-secondary mt-1">
                              {t('wallet.availableBalance')}: {formatCurrency(selectedWallet.available_balance, selectedWallet.currency_code || selectedWallet.currency)}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-primary mb-2">
                            {t('common.description')} ({t('common.optional')})
                          </label>
                          <Input
                            type="text"
                            placeholder={t('wallet.paymentDescription') || 'Descrição do pagamento'}
                            value={description}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
                            disabled={isSending}
                          />
                        </div>

                        <div className="flex space-x-4">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setSelectedContact(null)
                              setHasWallet(null)
                              setAmount('')
                              setDescription('')
                            }}
                            className="flex-1"
                            disabled={isSending}
                          >
                            {t('common.back') || 'Voltar'}
                          </Button>
                          <Button
                            type="button"
                            onClick={handleSendMoney}
                            className="flex-1"
                            disabled={
                              isSending ||
                              !amount ||
                              parseFloat(amount) <= 0 ||
                              parseFloat(amount) > maxAmount ||
                              hasWallet !== true
                            }
                          >
                            {isSending ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                {t('wallet.sending') || 'A enviar...'}
                              </>
                            ) : (
                              <>
                                <Send className="h-4 w-4 mr-2" />
                                {t('wallet.send') || 'Enviar'}
                              </>
                            )}
                          </Button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

